/* eslint-disable no-unused-vars */
import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Text,
  useToast,
  UseToastOptions,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUp, Eye, Trash } from 'phosphor-react';
import { useMutation } from '@tanstack/react-query';
import {
  IDocumentoCampos,
  TipoDocumentoRequest,
  useSocietario,
} from '../../../../../hooks/useSocietario';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { DocumentoPreviewDrawer } from './DocumentoPreviewDrawer';

interface DocumentoUploaderProps {
  // eslint-disable-next-line react/require-default-props
  defaultTipo?: TipoDocumentoRequest;
  // eslint-disable-next-line react/require-default-props
  contratoId?: number;
  // eslint-disable-next-line react/require-default-props
  socioId?: number;
  onExtracted: (campos: IDocumentoCampos) => void;
}

// Substitui `CnhUploader.tsx` em fluxos novos. Aceita CNH e RG, PDF/JPG/PNG.
// Implementa diferenciacao de erro:
// - 422 (extracao vazia): mensagem clara pro operador re-tentar com outro
//   arquivo. Calibracao 2026-05-19 mostrou que IA pode retornar tudo null
//   em casos como CamScanner overlay ou RG com prompt MRZ-first; UX antiga
//   confundia operador com 201+payload nulo.
// - 4xx genericos (formato invalido, content-type nao suportado): mensagem
//   amigavel do backend.
// - IA (DeepSeek) fora ou lenta, ou servidor sem resposta: tentar de novo ou
//   preencher a mao (`avisoDoErroDeLeitura`).
//
// Apos upload bem-sucedido, "Ver documento" abre Drawer top (sem overlay)
// pra operador conferir os campos extraidos contra o documento original.
// Decisao UX 2026-05-19: visualizacao no topo deixando form abaixo
// interativo — operador edita campos olhando o doc, sem fechar preview.

// Aviso de erro da leitura. 502 = a IA nao respondeu no teto da leitura; sem resposta, 503
// ou 504 = o servidor ficou sem responder (o ALB corta em 60 s, sem CORS). Nesses casos o
// documento nao tem culpa: "tente com outra imagem" mandaria o operador pelo caminho errado.
export const avisoDoErroDeLeitura = (err: any): UseToastOptions => {
  const status = err?.response?.status;
  const message = extractApiErrorMessage(err, 'Tente novamente com outra imagem.');
  // 422 = extracao vazia (mensagem ja vem boa do backend)
  if (status === 422) {
    return { status: 'warning', title: 'Documento ilegivel', description: message, duration: 6000 };
  }
  if (status === 502 || status === 503 || status === 504 || !err?.response) {
    return {
      status: 'error',
      title: 'Servico de extracao indisponivel',
      description:
        'A leitura nao respondeu a tempo. Tente de novo em alguns minutos ou preencha os dados a mao.',
      duration: 8000,
    };
  }
  return { status: 'error', title: 'Falha ao processar documento', description: message };
};

export const DocumentoUploader = ({
  defaultTipo = 'auto',
  contratoId,
  socioId,
  onExtracted,
}: DocumentoUploaderProps) => {
  const { analyzeDocumento } = useSocietario();
  const toast = useToast();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [tipo, setTipo] = useState<TipoDocumentoRequest>(defaultTipo);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: analyzeDocumento,
    onSuccess: res => {
      const campos = res.data?.campos;
      if (campos && (campos.nome || campos.cpf || campos.rg)) {
        onExtracted(campos);
        toast({
          status: 'success',
          title: 'Dados extraidos do documento',
          description: campos.tipo_documento
            ? `Tipo detectado: ${campos.tipo_documento.toUpperCase()}`
            : undefined,
        });
      } else {
        toast({ status: 'warning', title: 'Nenhum dado extraido' });
      }
    },
    onError: (err: any) => toast(avisoDoErroDeLeitura(err)),
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      setUploadedFile(file);
      try {
        await mutateAsync({
          file,
          tipo,
          contrato_id: contratoId,
          socio_id: socioId,
        });
      } catch {
        // Erro ja tratado no `onError` do useMutation acima (toast amigavel
        // diferenciando 422/502/etc). Capturado aqui pra evitar unhandled
        // promise rejection — sem isso, CRA DevServer mostra overlay
        // vermelho de erro alem do toast (UX confusa, especialmente em 422
        // que e fluxo esperado).
      }
    },
    [mutateAsync, tipo, contratoId, socioId],
  );

  const handleLimpar = () => {
    setUploadedFile(null);
    setIsPreviewOpen(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    disabled: isPending,
  });

  const fileName = uploadedFile?.name ?? null;

  return (
    <Box>
      <Flex gap={2} mb={2} align="center">
        <Text fontSize="sm" fontWeight="medium" minW="80px">
          Tipo:
        </Text>
        <Select
          size="sm"
          value={tipo}
          onChange={e => setTipo(e.target.value as TipoDocumentoRequest)}
          isDisabled={isPending}
          maxW="200px"
        >
          <option value="auto">Detectar automaticamente</option>
          <option value="cnh">CNH</option>
          <option value="rg">RG</option>
        </Select>
      </Flex>

      <Box
        {...getRootProps()}
        border="2px dashed"
        borderColor={isDragActive ? 'primary' : 'gray.300'}
        borderRadius="md"
        p={4}
        textAlign="center"
        cursor={isPending ? 'not-allowed' : 'pointer'}
        bg={isDragActive ? 'blue.50' : 'gray.50'}
        transition="all 0.15s"
        _hover={{ borderColor: 'primary', bg: 'blue.50' }}
      >
        <input {...getInputProps()} />
        {/* eslint-disable-next-line no-nested-ternary */}
        {isPending ? (
          <Flex direction="column" align="center" gap={2}>
            <Spinner size="md" color="primary" />
            <Text fontSize="sm">Analisando documento...</Text>
          </Flex>
        ) : fileName ? (
          <Flex direction="column" align="center" gap={1}>
            <Text fontSize="sm" fontWeight="medium">
              {fileName}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Solte outro arquivo para substituir
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" align="center" gap={2}>
            <CloudArrowUp size={28} />
            <Text fontSize="sm">Arraste o documento (CNH ou RG) ou clique para selecionar</Text>
            <Text fontSize="xs" color="gray.500">
              Aceita PDF, JPG e PNG
            </Text>
            <Text fontSize="2xs" color="gray.400" maxW="320px" textAlign="center">
              CNH digital (PDF Denatran), foto da CNH fisica ou RG (frente + verso). Selecione o
              tipo acima ou deixe em &quot;detectar&quot;.
            </Text>
          </Flex>
        )}
      </Box>
      {uploadedFile && !isPending && (
        <Flex justify="space-between" mt={1} gap={2}>
          <Button
            variant="outline"
            size="xs"
            leftIcon={<Eye size={14} />}
            onClick={() => setIsPreviewOpen(true)}
          >
            Ver documento
          </Button>
          <Button variant="ghost" size="xs" leftIcon={<Trash size={14} />} onClick={handleLimpar}>
            Limpar
          </Button>
        </Flex>
      )}

      <DocumentoPreviewDrawer
        file={uploadedFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </Box>
  );
};

export default DocumentoUploader;
